import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type PropsWithChildren, useCallback, useMemo, useRef } from "react";

import { ApiProblem, apiDownload, apiRequest, type Schemas } from "../lib/api/client";
import {
  roleInShop,
  SessionContext,
  type SessionContextValue,
  type ShopAccess,
  type SignInChallenge,
  type WorkspaceSession,
} from "./session";

const SESSION_KEY = ["session"] as const;

export type SessionProviderProps = PropsWithChildren<{
  /** Test seam: start in this state and skip the first check with the API. */
  initialSession?: WorkspaceSession;
}>;

/**
 * The browser side of password-plus-TOTP sign-in (P1-017, P1-019).
 *
 * What the API says about the session is a query: asked once on load, and
 * replaced by each sign-in step. The synchronizer token is held in a ref, in
 * memory only: never in storage, a cookie or the URL, and never rendered. It
 * rotates at every step, as the contract requires, and a reload asks the API
 * again rather than restoring anything.
 */
export function SessionProvider({ children, initialSession }: SessionProviderProps) {
  const queryClient = useQueryClient();
  const csrfToken = useRef<string | undefined>(undefined);

  const signedIn = useCallback(async (summary: Schemas["SessionSummary"]): Promise<WorkspaceSession> => {
    const actorId = summary.actor_id;
    // A Platform Superadmin holds no shop role (ADR 0030): the session's own
    // actor class decides, and there is no shop to ask about.
    if (summary.actor_class === "PLATFORM_SUPERADMIN") {
      return { actorId, kind: "signed-in", role: "superadmin", shop: null };
    }
    // A support operator (P1-013 slice 5) holds no shop role either: its
    // workspace lists the grants that name it (P5-006).
    if (summary.actor_class === "SUPPORT") {
      return { actorId, kind: "signed-in", role: "support", shop: null };
    }
    const [mine, legal, branding] = await Promise.all([
      apiRequest<Schemas["MyShops"]>("GET", "/v1/me/shops"),
      // Unlike branding, this one is not optional: if the terms cannot be
      // read, nobody can say whether the person has accepted them, so the
      // session is reported unavailable rather than let in (P1-023).
      apiRequest<Schemas["LegalAcceptances"]>("GET", "/v1/me/legal-acceptances"),
      // Branding only colours the shell: if it cannot be read, the default
      // theme is drawn and nobody is kept from signing in.
      apiRequest<Schemas["Branding"]>("GET", "/v1/branding").catch(() => null),
    ]);
    const first = mine.shops[0];
    const shop: ShopAccess | null = first
      ? {
          delegatedRights: first.delegated_rights,
          permissions: first.permissions,
          roles: first.roles,
          shopId: first.shop_id,
          tenantId: first.tenant_id,
        }
      : null;
    const legalOutstanding = legal.required.filter((item) => item.accepted_at === null);
    return { actorId, branding, kind: "signed-in", legalOutstanding, legalRequired: legal.required, role: roleInShop(shop), shop };
  }, []);

  const query = useQuery({
    enabled: initialSession === undefined,
    initialData: initialSession,
    queryFn: async (): Promise<WorkspaceSession> => {
      try {
        const current = await apiRequest<Schemas["CurrentSessionState"]>("GET", "/v1/auth/session");
        csrfToken.current = current.csrf_token;
        return current.state === "IDLE_LOCKED" ? { kind: "idle-locked" } : await signedIn(current.session);
      } catch (error) {
        csrfToken.current = undefined;
        if (error instanceof ApiProblem && error.status === 401) {
          return { kind: "signed-out" };
        }
        throw error;
      }
    },
    queryKey: SESSION_KEY,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  const { data, isError, refetch } = query;
  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const startSignIn = useCallback(async (email: string, password: string): Promise<SignInChallenge> => {
    const bootstrap = await apiRequest<Schemas["CSRFBootstrap"]>("GET", "/v1/auth/csrf");
    const challenge = await apiRequest<Schemas["SignInChallenge"]>("POST", "/v1/auth/sign-in/password", {
      body: { email, password },
      csrfToken: bootstrap.csrf_token,
    });
    csrfToken.current = challenge.csrf_token;
    return {
      expiresAt: challenge.expires_at,
      passkeyOptions: challenge.passkey_options,
      remainingAttempts: challenge.remaining_attempts,
      state: challenge.state,
    };
  }, []);

  const establish = useCallback(
    async (result: Schemas["SessionEstablished"]) => {
      csrfToken.current = result.csrf_token;
      queryClient.setQueryData<WorkspaceSession>(SESSION_KEY, await signedIn(result.session));
    },
    [queryClient, signedIn],
  );

  const completeSignIn = useCallback(
    async (code: string) => {
      const result = await apiRequest<Schemas["SessionEstablished"] | Schemas["PasskeyEnrolmentRequired"]>(
        "POST",
        "/v1/auth/sign-in/totp",
        { body: { totp_code: code }, csrfToken: csrfToken.current },
      );
      if (result.state === "PASSKEY_ENROLMENT_REQUIRED") {
        csrfToken.current = result.csrf_token;
        return { expiresAt: result.expires_at, options: result.passkey_options };
      }
      await establish(result);
      return null;
    },
    [establish],
  );

  const completePasskeySignIn = useCallback(
    async (credential: Schemas["PasskeyAssertion"]) =>
      establish(
        await apiRequest<Schemas["SessionEstablished"]>("POST", "/v1/auth/sign-in/passkey", {
          body: { credential },
          csrfToken: csrfToken.current,
        }),
      ),
    [establish],
  );

  const completePasskeyEnrolment = useCallback(
    async (credential: Schemas["PasskeyAttestation"], name?: string) =>
      establish(
        await apiRequest<Schemas["SessionEstablished"]>("POST", "/v1/auth/sign-in/passkey-enrolment", {
          body: name ? { credential, name } : { credential },
          csrfToken: csrfToken.current,
        }),
      ),
    [establish],
  );

  // An idle-locked session holds the unlock-scoped token from GET
  // /v1/auth/session; the password alone brings the session back (P1-024).
  const unlock = useCallback(
    async (password: string) => {
      const result = await apiRequest<Schemas["SessionEstablished"]>("POST", "/v1/auth/session/unlock", {
        body: { password },
        csrfToken: csrfToken.current,
      });
      await establish(result);
    },
    [establish],
  );

  const signOut = useCallback(async () => {
    try {
      await apiRequest("DELETE", "/v1/auth/session", { csrfToken: csrfToken.current });
    } catch (error) {
      // A session the API no longer knows is already signed out. Any other
      // failure leaves it signed in, and the caller says so.
      if (!(error instanceof ApiProblem && error.status === 401)) {
        throw error;
      }
    }
    csrfToken.current = undefined;
    // Whatever the signed-in user could see goes with them.
    queryClient.removeQueries({ predicate: (cached) => cached.queryKey[0] !== SESSION_KEY[0] });
    queryClient.setQueryData<WorkspaceSession>(SESSION_KEY, { kind: "signed-out" });
  }, [queryClient]);

  const request = useCallback(
    async <T,>(
      method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
      path: `/v1/${string}`,
      options: { body?: unknown; reauthorizationId?: string; operationId?: string } = {},
    ): Promise<T> => {
      const result = await apiRequest<T>(method, path, { ...options, csrfToken: csrfToken.current });
      const rotated = (result as { csrf_token?: unknown } | undefined)?.csrf_token;
      if (typeof rotated === "string") {
        csrfToken.current = rotated;
      } else if (method === "POST" && path === "/v1/auth/reauthorizations") {
        // A step-up replaces the session and so its synchronizer token. The
        // reply carries it (handled above); an older API returned it only
        // through GET /v1/auth/session, and without it the action that
        // follows is refused as 401. If the read fails, that action fails
        // as before and says so.
        try {
          const current = await apiRequest<Schemas["CurrentSessionState"]>("GET", "/v1/auth/session");
          csrfToken.current = current.csrf_token;
        } catch {
          // The next request reports the problem.
        }
      }
      return result;
    },
    [],
  );

  const updateBranding = useCallback(
    (branding: Schemas["Branding"]) =>
      queryClient.setQueryData<WorkspaceSession>(SESSION_KEY, (current) =>
        current?.kind === "signed-in" ? { ...current, branding } : current,
      ),
    [queryClient],
  );

  const updateLegal = useCallback(
    (legalRequired: readonly Schemas["LegalRequirement"][]) =>
      queryClient.setQueryData<WorkspaceSession>(SESSION_KEY, (current) =>
        current?.kind === "signed-in"
          ? { ...current, legalOutstanding: legalRequired.filter((item) => item.accepted_at === null), legalRequired }
          : current,
      ),
    [queryClient],
  );

  const download = useCallback<SessionContextValue["download"]>(
    (path, accept, options) => apiDownload(path, accept, { ...options, csrfToken: csrfToken.current, method: "POST" }),
    [],
  );

  const value = useMemo<SessionContextValue>(
    () => ({
      completePasskeyEnrolment,
      completePasskeySignIn,
      completeSignIn,
      download,
      refresh,
      request,
      session: data ?? (isError ? { kind: "unavailable" } : { kind: "checking" }),
      signOut,
      startSignIn,
      unlock,
      updateBranding,
      updateLegal,
    }),
    [completePasskeyEnrolment, completePasskeySignIn, completeSignIn, data, download, isError, refresh, request, signOut, startSignIn, unlock, updateBranding, updateLegal],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
